// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as storageActions from 'admin/storage/actions';
import { connect } from 'react-redux';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { Loader } from 'common/components';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import {
    Action,
    Icon,
    Translate,
} from '@plesk/ui-library';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import PageHeader from 'common/components/PageHeader/PageHeader';
import {
    PageSection,
    SubTitleRow,
    SubTitleSection,
    SubTitleSectionLabel,
} from 'common/components/PageHeader/Styles';
import { ICONS } from 'common/constants';
import { STORAGE_TYPES_TRANSLATION_MAP } from 'common/api/resources/StorageType';
import ComputeResourceVmTable
    from 'admin/computeResourceVm/containers/ComputeResourceVmTable/ComputeResourceVmTable';
import { EmptyView } from 'common/components/EmptyView/EmptyView';

export type StorageServersPageProps =
    RouteComponentProps<{ id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const StorageServersPage: React.FC<StorageServersPageProps> = ({
    storage,
    isLoading,
    storageActions: { getStorage },
    match: { params },
}) => {
    const storageId = parseInt(params.id, 10);

    const onMount = async () => {
        await getStorage(storageId);
    };

    const renderTitle = () => (
        <PageSection>
            <Action component={Link} to={pathTo('storage')}>
                <Translate content="storage.page.breadcrumb" />
            </Action>
            <Icon name={ICONS.CHEVRON_RIGHT} />
            <Action component={Link} to={pathTo(`storage/${storageId}`)}>
                {storage.name}
            </Action>
            <Icon name={ICONS.CHEVRON_RIGHT} />
            <Translate content="storage.page.stats.virtualServers" />
        </PageSection>
    );

    const renderSubtitle = () => (
        <SubTitleRow>
            <SubTitleSection>
                <SubTitleSectionLabel>
                    <Translate content="storage.page.type" />
                </SubTitleSectionLabel>
                {STORAGE_TYPES_TRANSLATION_MAP[storage.type.name]}
            </SubTitleSection>
            {storage.storage_tag && (
                <SubTitleSection>
                    <SubTitleSectionLabel>
                        <Translate content="storage.page.storageTier" />
                    </SubTitleSectionLabel>
                    {storage.storage_tag.name}
                </SubTitleSection>
            )}
        </SubTitleRow>
    );

    return (
        <WithErrorPages onMount={onMount} deps={[storageId]}>
            <Loader isLoading={isLoading}>
                <PageHeader
                    title={renderTitle()}
                    subtitle={renderSubtitle()}
                />
                <ComputeResourceVmTable
                    storageId={storageId}
                    withFilters={true}
                    emptyView={
                        <EmptyView
                            title="computeResource.servers.emptyView.title"
                            description="computeResource.servers.emptyView.description"
                        />
                    }
                />
            </Loader>
        </WithErrorPages>
    );
};

const mapStateToProps = (state: RootState) => ({
    storage: state.storage.item,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.STORAGE_GET),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    storageActions: bindActionCreators(storageActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StorageServersPage));
